import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { useUpdateAccountingHeader } from 'src/features/accountingHeaders/api/useUpdateAccountingHeader';
import { LIST_ACCOUNTING_HEADINGS_RESOURCE } from 'src/features/accountingHeaders/api/useListAccountingHeadings';
import { accountingHeadersMessages } from 'src/features/accountingHeaders/utils/messages';
import AccountingHeaderFormModal from './AccountingHeaderFormModal';
import { AccountingHeading } from 'src/features/accountingHeaders/utils/types';

interface UpdateAccountingHeaderFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAccountingHeader: AccountingHeading;
    children?: React.ReactNode;
}

function UpdateAccountingHeaderFormModal<TData>({
                                                   className,
                                                   semantics,
                                                   children,
                                                   selectedAccountingHeader,
                                               }: UpdateAccountingHeaderFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateAccountingHeader({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ACCOUNTING_HEADINGS_RESOURCE] });
            toast({
                title: formatMessage(accountingHeadersMessages.updateAccountingHeaderSuccess),
                description: formatMessage(accountingHeadersMessages.updateAccountingHeaderSuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <AccountingHeaderFormModal
            selectedAccountingHeader={selectedAccountingHeader}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, id: selectedAccountingHeader.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(accountingHeadersMessages.editAccountingHeaderSubtitle, {
                entity: selectedAccountingHeader.name,
            })}
        >
            {children}
        </AccountingHeaderFormModal>
    );
}

export default UpdateAccountingHeaderFormModal;
